import type { Mark } from '@/game/types';

const THEME_KEY = 'velhazap:theme';
const LOCALE_KEY = 'velhazap:locale';
const gameKey = (id: string) => `velhazap:game:${id}`;

export type ThemePreference = 'system' | 'light' | 'dark';
export type LocaleCode = 'pt' | 'en';

export interface GameLocalRecord {
  role: Mark;
  moveCount: number;
  nick?: string;
}

function canUseStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function getThemePreference(): ThemePreference {
  if (!canUseStorage()) return 'system';
  const value = localStorage.getItem(THEME_KEY);
  if (value === 'light' || value === 'dark' || value === 'system') return value;
  return 'system';
}

export function setThemePreference(theme: ThemePreference): void {
  if (!canUseStorage()) return;
  localStorage.setItem(THEME_KEY, theme);
}

export function detectBrowserLocale(): LocaleCode {
  if (typeof navigator === 'undefined') return 'pt';
  const lang = navigator.language.toLowerCase();
  return lang.startsWith('en') ? 'en' : 'pt';
}

export function getLocalePreference(): LocaleCode | null {
  if (!canUseStorage()) return null;
  const value = localStorage.getItem(LOCALE_KEY);
  if (value === 'pt' || value === 'en') return value;
  return null;
}

export function setLocalePreference(locale: LocaleCode): void {
  if (!canUseStorage()) return;
  localStorage.setItem(LOCALE_KEY, locale);
}

export function getGameRecord(id: string): GameLocalRecord | null {
  if (!canUseStorage()) return null;
  const raw = localStorage.getItem(gameKey(id));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as GameLocalRecord;
    if (parsed.role !== 'X' && parsed.role !== 'O') return null;
    if (typeof parsed.moveCount !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveGameRecord(id: string, record: GameLocalRecord): void {
  if (!canUseStorage()) return;
  localStorage.setItem(gameKey(id), JSON.stringify(record));
}

export function touchMoveCount(id: string, moveCount: number, role?: Mark, nick?: string): void {
  const existing = getGameRecord(id);
  const next: GameLocalRecord = {
    role: role ?? existing?.role ?? 'X',
    moveCount: Math.max(existing?.moveCount ?? 0, moveCount),
    nick: nick ?? existing?.nick,
  };
  saveGameRecord(id, next);
}

export function isStaleLink(id: string, moveCount: number): boolean {
  const existing = getGameRecord(id);
  if (!existing) return false;
  return moveCount < existing.moveCount;
}

export function resolveRoleFromNames(
  nameX: string,
  nameO: string,
  localNick: string | undefined,
  existingRole: Mark | undefined,
): Mark | null {
  if (existingRole) return existingRole;
  if (!localNick) return null;
  if (localNick === nameX) return 'X';
  if (nameO && localNick === nameO) return 'O';
  return null;
}
